import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { HooksService, InfoModal } from 'basic-components';
import Query from 'dashboard-services/api/Query';
import { CommonService } from 'dashboard-services/services';
import { globalActions } from 'primary-components';

import { pdfViewerActions } from 'actions/modules/tools';
import getApiConfig from 'api/ApiConfig';
import { filesDatalakeQueries } from 'queries/modules/datalake';
import { sendEvent } from 'services/GAService';

import PdfCore from './PdfCore';
import PdfZoomer from './PdfZoomer';

import { GlobalWorkerOptions } from 'pdfjs-dist';

import './PdfViewer.scss';

const workerUrl = new URL('pdfjs-dist/build/pdf.worker.mjs', import.meta.url).pathname;
GlobalWorkerOptions.workerSrc = workerUrl;

const PdfViewer = () => {
  const isMounted = HooksService.useIsMounted()
  const dispatch = useDispatch(),
        [content, setContent] = useState(undefined),
        [fileInfo, setFileInfo] = useState({}), 
        [scale, setScale] = useState(1),
        { fileId, provider, open } = useSelector(state => state.toolsState.pdfViewerState),
        getQueryBuilder = useCallback(() => 
          new Query.Builder(dispatch(getApiConfig()))
            .withOnParse(response => Promise.resolve(response))
            .withHeader("Target", provider, true)
        , [dispatch, provider]),
        getQueryBuilderS3 = useCallback(() => 
          new Query.Builder(dispatch(getApiConfig()))
            .withOnParse(response => Promise.resolve(response))
        , [dispatch]),
        { data: infoResponse } = filesDatalakeQueries.useGetFileDefinition({ getQueryBuilder, id: fileId, refetchOnMount: false, refetchOnReconnect: false, refetchOnWindowFocus: false }),
        location = useMemo(() => infoResponse?.headers?.get("Location"), [infoResponse]),
        { mutateAsync: s3GetCall } = filesDatalakeQueries.useGetFileContentMutation({ getQueryBuilder: getQueryBuilderS3, url: location }),
        downloadPdf = useCallback(() => {
          const reqId = CommonService.generateUID()
          dispatch(globalActions.handleRequest({ id: reqId }))
          return s3GetCall()
            .then(response => response.arrayBuffer())
            .then(response => isMounted.current && import('buffer').then(({ Buffer }) => setContent(Buffer(response).toString('base64'))))
            .finally(() => dispatch(globalActions.handleRequest({ id: reqId, isResponse: true })))
        }, [dispatch, s3GetCall, isMounted]),
        close = useCallback(() => {
          dispatch(pdfViewerActions.changeValue(undefined, "fileId"))
          dispatch(pdfViewerActions.changeValue(false, "open"))
          dispatch(pdfViewerActions.changeValue(undefined, "provider"))
        }, [dispatch]),
        optionalText = useMemo(() => {
          const groupName = fileInfo?.groupName
          if(groupName === undefined) {
            return `File name: ${fileInfo?.fileName}`
          } else {
            return `${fileInfo?.groupName}: ${fileInfo?.fileName}`
          }
        }, [fileInfo])
        
  useEffect(() => {
    if(location) {
      downloadPdf()
    }
  }, [downloadPdf, location])

  useEffect(() => {
    const event = {
      category: "view_item",
      action: "pdf_preview",
      label: "tool",
    };
    dispatch(sendEvent(event))
  }, [dispatch])

  useEffect(() => {
    if(!infoResponse?.bodyUsed) {
      infoResponse?.json().then(r => setFileInfo(r || {}))
    }
  }, [infoResponse])

  return (
    <InfoModal
        className="ng-pdf-viewer"
        onClose={close}
        open={open}
        optionalText={optionalText}
        size="large"// we still override this in scss
        sizeWidth="large" // we still override this scss
        title="PDF Viewer"
    >
      <PdfZoomer
          scale={scale}
          setScale={setScale}
      />
      <PdfCore
          content={content}
          onCancel={close}
          scale={scale}
      />
    </InfoModal>
  )
}

export default memo(PdfViewer);